import React, { useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import theme from "theme";
import IntentPathsIllustration from "components/marketing/IntentPathsIllustration";
import { hero, sectionIds } from "content/site";
import {
	Container,
	Section,
	PrimaryButton,
	SecondaryButton,
} from "components/marketing/primitives";

const Wrap = styled(Section)`
	padding-top: clamp(2.25rem, 6vw, 4.25rem);
	padding-bottom: clamp(2.5rem, 6vw, 4rem);
	background: ${theme.background.heroWash};
	border-bottom: 1px solid ${theme.color.border};
`;

const Grid = styled.div`
	display: grid;
	grid-template-columns: 1fr;
	gap: 2rem;
	align-items: start;
	@media (min-width: 900px) {
		grid-template-columns: 1fr 1fr;
		grid-template-rows: auto auto;
		column-gap: 3rem;
		row-gap: 0.75rem;
		align-items: start;
	}
`;

const Kicker = styled.p`
	font-family: ${theme.fontFamily.sans};
	font-size: 0.8125rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.08em;
	color: ${theme.color.primary};
	margin: 0 0 0.75rem;
	@media (min-width: 900px) {
		grid-column: 1;
		grid-row: 1;
	}
`;

const Title = styled.h1`
	font-family: ${theme.fontFamily.sans};
	font-size: clamp(2.125rem, 5vw, 3.25rem);
	font-weight: 800;
	line-height: 1.08;
	color: ${theme.color.dark};
	margin: 0 0 1rem;
	letter-spacing: -0.035em;
`;

const HERO_VISUAL_MIN_PX = 520;

const HeroCopy = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	box-sizing: border-box;
	/* Former trust-chip row (~1.25rem) + margin (1.5rem), split above/below */
	padding-block: 1.375rem;
	@media (min-width: 900px) {
		grid-column: 1;
		grid-row: 2;
		align-self: start;
		min-width: 0;
		min-height: ${HERO_VISUAL_MIN_PX}px;
	}
`;

const Subhead = styled.p`
	font-family: ${theme.fontFamily.sans};
	font-size: 1.0625rem;
	line-height: 1.55;
	color: ${theme.color.darkL2};
	margin: 0 0 1.25rem;
	max-width: 36rem;
`;

const CtaRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 0.75rem;
	align-items: center;
`;

const Visual = styled.figure`
	border-radius: ${theme.radius.xl};
	background: ${theme.color.darkL1};
	border: 1px solid rgba(255, 255, 255, 0.08);
	min-height: clamp(300px, 58vw, 440px);
	margin: 0;
	position: relative;
	overflow: hidden;
	box-shadow:
		${theme.boxShadow.xxl},
		inset 0 1px 0 rgba(255, 255, 255, 0.06);
	display: flex;
	flex-direction: column;
	@media (min-width: 900px) {
		grid-column: 2;
		grid-row: 2;
		align-self: start;
		min-height: ${HERO_VISUAL_MIN_PX}px;
		height: ${(p) => (p.$panelHeight != null ? `${p.$panelHeight}px` : "auto")};
		max-height: ${(p) => (p.$panelHeight != null ? `${p.$panelHeight}px` : "none")};
	}
	&::after {
		content: "";
		position: absolute;
		inset: 0;
		background: linear-gradient(
			135deg,
			rgba(0, 119, 204, 0.28) 0%,
			transparent 48%,
			rgba(255, 196, 0, 0.14) 100%
		);
		pointer-events: none;
	}
`;

const VisualStack = styled.div`
	position: relative;
	z-index: 1;
	padding: 1.25rem 1.35rem 1.35rem;
	flex: 1;
	min-height: 0;
	display: flex;
	flex-direction: column;
	@media (min-width: 900px) {
		padding-top: clamp(0.75rem, 2vw, 1.25rem);
		padding-bottom: clamp(0.75rem, 2vw, 1.25rem);
		padding-left: clamp(1rem, 2vw, 1.35rem);
		padding-right: clamp(1rem, 2vw, 1.35rem);
	}
`;

export default function Hero() {
	const copyRef = useRef(null);
	const [panelHeight, setPanelHeight] = useState(null);

	useLayoutEffect(() => {
		const el = copyRef.current;
		if (!el) return undefined;

		const mq = window.matchMedia("(min-width: 900px)");

		const update = () => {
			if (!mq.matches) {
				setPanelHeight(null);
				return;
			}
			setPanelHeight(Math.max(el.getBoundingClientRect().height, HERO_VISUAL_MIN_PX));
		};

		update();
		const ro = new ResizeObserver(update);
		ro.observe(el);
		mq.addEventListener("change", update);
		return () => {
			ro.disconnect();
			mq.removeEventListener("change", update);
		};
	}, []);

	return (
		<Wrap id={sectionIds.hero} aria-labelledby="hero-heading">
			<Container>
				<Grid>
					<Kicker>The Security Information Exchange</Kicker>
					<HeroCopy ref={copyRef}>
						<Title id="hero-heading">{hero.title}</Title>
						<Subhead>{hero.subhead}</Subhead>
						{hero.ctas && hero.ctas.length > 0 ? (
							<CtaRow>
								{hero.ctas.map((cta) =>
									cta.variant === "primary" ? (
										<PrimaryButton key={cta.href} href={cta.href}>
											{cta.label}
										</PrimaryButton>
									) : (
										<SecondaryButton key={cta.href} href={cta.href}>
											{cta.label}
										</SecondaryButton>
									)
								)}
							</CtaRow>
						) : null}
					</HeroCopy>
					<Visual
						$panelHeight={panelHeight}
						aria-label="How Truss works: an animated diagram shows one signal moving from distributed information sources into the Security Information Exchange, through dashboard exploration, REST, STIX, and MCP integration, into security tools, then widening to the Security Information Ecosystem."
					>
						<VisualStack>
							<IntentPathsIllustration />
						</VisualStack>
					</Visual>
				</Grid>
			</Container>
		</Wrap>
	);
}
